import { Box, Flex, Icon } from '@chakra-ui/react';
import { useState } from 'react';
import { SpinnerLoader } from '@kula-ai/components';
import { Eye, XClose } from '@kula-ai/icons-core';
import { PreviewPdfModal } from '@/components/ats/FileUpload/PreviewPdfModal';
import { canPreview } from '@/components/ats/FileUpload/previewSupport';
import { fileTypeIcon } from '@/components/shared/attachments/fileTypeIcon';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { KText } from '@/ui-library/components/text';
import { formatBytes } from '@/utils/format-bytes';
import type { AttachmentChip } from './attachDocumentSelection';

export interface SelectedAttachmentChipsProps {
  chips?: AttachmentChip[];
  onRemove?: (_key: string) => void;
}

const REMOVE_LABEL = 'Remove attachment';
const PREVIEW_LABEL = 'Preview attachment';

// Figma 19436:15516 — a fixed card, so a row of them lines up whatever the
// names are, rather than each sizing to its own filename.
const CARD_WIDTH = '212px';
const CARD_HEIGHT = '56px';
const TILE_SIZE = '44px';

/** What the eye would open: an upload's own bytes, or the document's link. */
const previewTarget = (chip: AttachmentChip) =>
  chip.file ?? {
    filename: chip.name,
    content_type: chip.contentType,
    // An explicit '' tells `canPreview` there is nothing to open — without it a
    // typed-but-linkless chip (the scorecards export) would offer an eye that
    // renders a blank viewer.
    url: chip.url ?? '',
  };

/**
 * The documents chosen for this email, shown below the composer.
 *
 * A card is usually a *reference* to one of the candidate's documents — nothing
 * is uploaded here, and the file is only read server-side when the email goes
 * out. So a card shows what the reference carries: the type as its tile, the
 * size, and a link to read the file before committing to send it. The scorecards
 * export has none of those — it does not exist until send time — and renders as
 * a name alone.
 */
export const SelectedAttachmentChips = ({
  chips = [],
  onRemove = () => {},
}: SelectedAttachmentChipsProps) => {
  const [previewKey, setPreviewKey] = useState<string | null>(null);

  if (!chips.length) return null;

  const previewing = chips.find((chip) => chip.key === previewKey);

  return (
    <>
      <Flex
        gap="5px"
        flexWrap="wrap"
        // The composer wraps this slot in its own bordered box, so the padding
        // is what keeps the cards off that border on every side — the Figma
        // strip's own top rule is that box's border.
        p="8px"
        data-test-id="selected-attachment-chips"
      >
        {chips.map((chip) => {
          // `formatBytes` answers '' for a missing or zero size; the line is
          // dropped rather than rendered empty, so a card with no size keeps
          // its name vertically centred.
          const size = formatBytes(chip.byteSize);
          // Nothing to open while the file is still being produced, and no type to
          // draw a tile from either.
          const previewable = !chip.loading && canPreview(previewTarget(chip));

          return (
            <Flex
              key={chip.key}
              alignItems="center"
              gap="12px"
              w={CARD_WIDTH}
              h={CARD_HEIGHT}
              px="8px"
              py="6px"
              border="1px solid"
              borderColor="border-color"
              borderRadius="6px"
              bg="base-white"
              data-test-id={`attachment-chip-${chip.key}`}
            >
              {chip.loading ? (
                // The loader takes the tile's place rather than the actions': the
                // file's kind is not settled yet, while the name already is.
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  w={TILE_SIZE}
                  h={TILE_SIZE}
                  flexShrink={0}
                  borderRadius="6px"
                  bg="secondary-50"
                  data-test-id={`attachment-chip-loading-${chip.key}`}
                >
                  <SpinnerLoader size="sm" />
                </Flex>
              ) : (
                <Icon
                  as={fileTypeIcon(chip.contentType)}
                  w={TILE_SIZE}
                  h={TILE_SIZE}
                  flexShrink={0}
                  data-test-id={`attachment-chip-tile-${chip.key}`}
                />
              )}

              {/* minW=0, or the truncation never engages: a flex item defaults
                  to min-width:auto and refuses to shrink past its content. */}
              <Flex
                direction="column"
                justifyContent="center"
                minW={0}
                flex={1}
              >
                <TruncatedTextWithTooltip
                  text={chip.name}
                  textProps={{ size: 'sm-medium', color: 'secondary-900' }}
                />
                {size && (
                  <KText size="xs-regular" color="secondary-400">
                    {size}
                  </KText>
                )}
              </Flex>

              <Flex alignItems="center" gap="4px" flexShrink={0}>
                {previewable && (
                  <Box
                    as="button"
                    type="button"
                    aria-label={`${PREVIEW_LABEL}: ${chip.name}`}
                    onClick={() => setPreviewKey(chip.key)}
                    lineHeight={0}
                    color="secondary-400"
                    _hover={{ color: 'secondary-600' }}
                    data-test-id={`attachment-chip-preview-${chip.key}`}
                  >
                    <Eye size={18} />
                  </Box>
                )}
                <Box
                  as="button"
                  type="button"
                  aria-label={`${REMOVE_LABEL}: ${chip.name}`}
                  onClick={() => onRemove(chip.key)}
                  lineHeight={0}
                  color="secondary-400"
                  _hover={{ color: 'secondary-600' }}
                  data-test-id={`attachment-chip-remove-${chip.key}`}
                >
                  <XClose size={18} />
                </Box>
              </Flex>
            </Flex>
          );
        })}
      </Flex>
      {previewing && (
        <PreviewPdfModal
          isOpen
          onClose={() => setPreviewKey(null)}
          file={previewTarget(previewing)}
        />
      )}
    </>
  );
};
